import { useEffect, useRef } from 'react';

function useScrollAnimation(ref: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('visible');
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
}

const ngRules = [
  { icon: 'ri-goblet-line', text: '紙たばこの喫煙', note: '※違約金10,000円（＋損害費用を請求する場合がございます）' },
  { icon: 'ri-tools-line', text: '設備・備品の破損・汚損' },
  { icon: 'ri-time-line', text: '無断での時間延長・1卓6名を超えるご利用' },
  { icon: 'ri-delete-bin-line', text: '使用用具の放置・ごみの放置' },
];

const notes = [
  {
    icon: 'ri-information-line',
    text: '入室前に予約確認メールの案内をご確認ください。',
  },
  {
    icon: 'ri-tools-line',
    text: '設備に不具合がある場合はメールにてご連絡ください。',
  },
  {
    icon: 'ri-money-cny-circle-line',
    text: '万が一設備を破損・汚損された場合、実費をご請求させていただく場合がございます。',
  },
  {
    icon: 'ri-group-line',
    text: '1卓あたりの上限は6名です。予約人数を超えるご利用は固くお断りしております。',
  },
];

export default function RulesSection() {
  const titleRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const notesRef = useRef<HTMLDivElement>(null);

  useScrollAnimation(titleRef as React.RefObject<HTMLElement>);
  useScrollAnimation(cardRef as React.RefObject<HTMLElement>);
  useScrollAnimation(notesRef as React.RefObject<HTMLElement>);

  return (
    <section id="rules" className="bg-[#08090d] py-20 md:py-28">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <div ref={titleRef} className="fade-in-section text-center mb-14">
          <p className="section-label mb-3">RULES</p>
          <h2 className="font-playfair font-bold text-white text-3xl md:text-4xl mb-4">
            ご利用ルール
          </h2>
          <p className="text-white/50 font-noto text-sm md:text-base max-w-md mx-auto">
            快適なご利用のため、以下の禁止事項をお守りください。
          </p>
        </div>

        {/* NG card */}
        <div ref={cardRef} className="fade-in-section mb-6">
          <div className="bg-[#2a0f0f] rounded-2xl p-8 border border-[#7a2a2a]/40">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 flex items-center justify-center bg-red-500/20 rounded-full">
                <i className="ri-close-circle-line text-red-400 text-2xl" />
              </div>
              <div>
                <h3 className="font-playfair font-bold text-white text-2xl">🚫 禁止事項</h3>
                <p className="text-white/50 text-xs font-noto mt-0.5">以下の行為は固くお断りしております</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {ngRules.map((rule) => (
                <div key={rule.text} className="flex items-start gap-3 bg-[#3a1515]/60 rounded-xl p-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-red-500/20 rounded-full shrink-0 mt-0.5">
                    <i className={`${rule.icon} text-red-400 text-sm`} />
                  </div>
                  <div>
                    <span className="text-white/80 text-sm font-noto leading-relaxed">{rule.text}</span>
                    {rule.note && (
                      <p className="text-red-400/80 text-xs font-noto mt-1 leading-relaxed">{rule.note}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Notes */}
        <div
          ref={notesRef}
          className="fade-in-section bg-[#1a1f2e] rounded-2xl p-6 border border-[#2a2f3e]"
          style={{ transitionDelay: '100ms' }}
        >
          <h4 className="text-white/80 font-noto font-semibold text-sm mb-4 flex items-center gap-2">
            <i className="ri-file-list-3-line text-[#d4af37]" />
            注意事項
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {notes.map((note) => (
              <div key={note.text} className="flex items-start gap-3">
                <div className="w-6 h-6 flex items-center justify-center shrink-0 mt-0.5">
                  <i className={`${note.icon} text-[#d4af37] text-sm`} />
                </div>
                <p className="text-white/50 text-xs font-noto leading-relaxed">{note.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
